import { CalApiError } from "@/lib/cal/types";

export const CAL_USER_UNABLE_TO_REFRESH_MESSAGE =
  "Cal.com user unable to refresh. Please reconnect this calendar or contact support.";

export const CAL_SCHEDULE_REJECTED_MESSAGE =
  "We couldn't prepare this calendar because Cal.com rejected the provider's schedule. " +
  "This usually means the provider's Cal.com account timezone is out of sync with their working hours. " +
  "We've logged the issue and will repair it on the next attempt — please try again shortly, " +
  "or contact support if this keeps happening.";

export const CALENDAR_AVAILABILITY_PREPARATION_FAILED_MESSAGE =
  "We couldn't prepare calendar availability for this appointment. " +
  "Our team has been notified. Please try again in a moment, and reach out to support if this persists.";

export class CalUserUnableToRefreshError extends Error {
  constructor() {
    super(CAL_USER_UNABLE_TO_REFRESH_MESSAGE);
    this.name = "CalUserUnableToRefreshError";
  }
}

export function isCalUserUnableToRefreshMessage(message?: string): boolean {
  return message === CAL_USER_UNABLE_TO_REFRESH_MESSAGE;
}

export function isCalScheduleRejectedMessage(message?: string): boolean {
  return message === CAL_SCHEDULE_REJECTED_MESSAGE;
}

/**
 * 4xx responses from Cal's schedule endpoints that are not auth-related
 * indicate a data/validation problem (most commonly a timezone mismatch
 * that causes invalid HH:MM values to be generated). Surface a specific
 * message so operators and support can distinguish this from generic
 * "something went wrong" failures.
 */
function isCalScheduleValidationError(error: unknown): boolean {
  if (!(error instanceof CalApiError)) {
    return false;
  }
  if (error.statusCode < 400 || error.statusCode >= 500) {
    return false;
  }
  if (error.statusCode === 401 || error.statusCode === 403 || error.statusCode === 429) {
    return false;
  }
  return true;
}

/**
 * Maps effective-availability sync failures to the safest user-facing message.
 */
export function getEffectiveAvailabilitySyncFailureMessage(error: unknown): string {
  if (typeof error === "string") {
    if (isCalUserUnableToRefreshMessage(error)) {
      return error;
    }
    if (isCalScheduleRejectedMessage(error)) {
      return error;
    }
  }

  if (error instanceof Error) {
    if (isCalUserUnableToRefreshMessage(error.message)) {
      return error.message;
    }
    if (isCalScheduleRejectedMessage(error.message)) {
      return error.message;
    }
  }

  if (isCalScheduleValidationError(error)) {
    return CAL_SCHEDULE_REJECTED_MESSAGE;
  }

  return CALENDAR_AVAILABILITY_PREPARATION_FAILED_MESSAGE;
}
